import {
  extractCSSUnitNumericValue,
  useTruncatedStyles,
  WbButton,
  WbCardContent,
  WbMarkdownHelperText,
  WbTable,
  WbTooltip,
  WbWidget,
} from '@agilelab/plugin-wb-platform';
import type { TableCellProps } from '@agilelab/plugin-wb-platform';
import { useWittyTable } from '@agilelab/plugin-wb-witty-react';
import { IconButton, TableBody, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircle';
import RemoveIcon from '@material-ui/icons/Delete';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import { ArrayFieldTemplateProps } from '@rjsf/utils';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  Children,
  Fragment,
  cloneElement,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { createScaffolderLayout } from '../../../layouts';
import { scaffolderPlugin } from '../../../plugin';
import {
  UI_STYLE_SAFE_KEY,
  UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY,
} from '../../utils';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  rowButtonsCell: {
    position: 'sticky',
    right: 0,
    background: theme.palette.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderTop: 'none',
    borderRight: 'none',
    padding: '0 12px',
  },
  iconsContainer: {
    display: 'flex',
    justifyContent: 'center',
    columnGap: theme.spacing(1),
    alignItems: 'center',
  },
  divider: {
    height: '20px',
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    margin: '0px 5px',
  },
}));

const DEFAULT_COL_CONTENT_MIN_WIDTH = 200;
const WIDE_FIELD_CONTENT_MIN_WIDTH = 450;

// Fields that render with wide content by default (e.g. pickers with entity search)
const WIDE_FIELDS = new Set(['CustomUrlPicker', 'EntitySearchPicker']);

// Fields that render with a default content width by default (textual pickers)
const FIELDS_WITH_DEFAULT_COLUMN_WIDTH = new Set([
  'WittyTextPicker',
  'RegexPicker',
  'RbacRolePicker',
  'FilePicker',
  'EntityRelationsPicker',
  'IdentitiesPicker',
  'EntityPicker',
  'EntitySelectionPicker',
  'OwnedEntityPicker',
  'AccessControlListPicker',
  'ComponentIdentifierPicker',
  'DescriptorPicker',
]);

// Schema types whose default RJSF widget is textual
const TEXT_WIDGET_SCHEMA_TYPES = new Set(['string', 'number', 'integer']);

interface TableTemplateProperties {
  maxDescriptionRows: number | undefined;
}

function parseTableTemplateProperties(
  props: ArrayFieldTemplateProps,
): TableTemplateProperties {
  const inputMaxDescriptionRows =
    props.uiSchema?.['ui:options']?.maxDescriptionRows;
  const maxDescriptionRows =
    inputMaxDescriptionRows &&
    Number.isFinite(inputMaxDescriptionRows) &&
    (inputMaxDescriptionRows as number) > 0
      ? (inputMaxDescriptionRows as number)
      : undefined;
  return {
    maxDescriptionRows,
  };
}

function computeDefaultColumnWidth(
  columnUiSchema?: Record<string, any>,
  columnSchema?: Record<string, any>,
): { width?: number; minWidth?: number; maxWidth?: number } {
  // Helper to build the return object for column widths
  function applyDefault(
    defaultMinWidth: number,
    templateMinWidth?: number,
    templateWidth?: number,
    templateMaxWidth?: number,
  ) {
    /**
     * If we provide a default minWidth, we ensure that if the template sets a maxWidth
     * that is smaller than the default minWidth (and the template doesn't specify any minWidth to override the default one), we use the template maxWidth as
     * the default minWidth instead to avoid exceeding user defined maxWidth (because in css, if we provide minWidth > maxWidth, minWidth wins).
     */
    let resolvedMinWidth = templateMinWidth ?? defaultMinWidth;
    if (templateMaxWidth !== undefined && templateMaxWidth < defaultMinWidth) {
      resolvedMinWidth = templateMaxWidth;
    }
    return {
      minWidth: resolvedMinWidth,
      width: templateWidth,
      maxWidth: templateMaxWidth,
    };
  }

  const fieldStyle = columnUiSchema?.[UI_STYLE_SAFE_KEY] as
    | Record<string, any>
    | undefined;
  const customField = columnUiSchema?.['ui:field'] as string | undefined;

  const userWidth = extractCSSUnitNumericValue(fieldStyle?.width);
  const userMinWidth = extractCSSUnitNumericValue(fieldStyle?.minWidth);
  const userMaxWidth = extractCSSUnitNumericValue(fieldStyle?.maxWidth);

  // Determine default width for fields based on schema (guard style, only one scenario can match)
  const field = customField ?? '';
  if (WIDE_FIELDS.has(field)) {
    return applyDefault(
      WIDE_FIELD_CONTENT_MIN_WIDTH,
      userMinWidth,
      userWidth,
      userMaxWidth,
    );
  }
  if (customField && FIELDS_WITH_DEFAULT_COLUMN_WIDTH.has(customField)) {
    return applyDefault(
      DEFAULT_COL_CONTENT_MIN_WIDTH,
      userMinWidth,
      userWidth,
      userMaxWidth,
    );
  }
  if (!customField) {
    // Apply a default minWidth of 300 for array fields using DefaultArrayFieldTemplate
    const schemaType = columnSchema?.type as string | undefined;
    if (schemaType === 'array') {
      return applyDefault(300, userMinWidth, userWidth, userMaxWidth);
    }
    // Otherwise, preserve previous logic for textual types
    if (!schemaType || TEXT_WIDGET_SCHEMA_TYPES.has(schemaType)) {
      return applyDefault(
        DEFAULT_COL_CONTENT_MIN_WIDTH,
        userMinWidth,
        userWidth,
        userMaxWidth,
      );
    }
  }

  // Fallback: only user-specified widths
  return {
    width: userWidth,
    minWidth: userMinWidth,
    maxWidth: userMaxWidth,
  };
}

const RowButtons = (props: ArrayFieldTemplateProps['items'][0]) => {
  const witty = useWittyTable();

  const classes = useStyles();
  return (
    <td className={classes.rowButtonsCell}>
      <div className={classes.iconsContainer}>
        {(props.hasMoveUp || props.hasMoveDown) && (
          <IconButton
            color="secondary"
            size="small"
            disabled={props.disabled || props.readonly || !props.hasMoveUp}
            onClick={props.onReorderClick(props.index, props.index - 1)}
            style={{ padding: 0 }}
            aria-label="reorder up"
          >
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
        )}

        {(props.hasMoveUp || props.hasMoveDown) && (
          <IconButton
            color="secondary"
            size="small"
            disabled={props.disabled || props.readonly || !props.hasMoveDown}
            onClick={props.onReorderClick(props.index, props.index + 1)}
            style={{ padding: 0 }}
            aria-label="reorder down"
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
        )}

        {(props.hasMoveUp || props.hasMoveDown) && (
          <div className={classes.divider} />
        )}

        {props.hasRemove && (
          <IconButton
            size="small"
            color="secondary"
            disabled={props.disabled || props.readonly}
            aria-label="reset fields"
            onClick={() => {
              // Reset the witty fields when removing a row so that it triggers all listeners to resubscribe to the WittyTable
              witty?.updateWittyFields({ type: 'RESET_FIELDS' });
              props.onDropIndexClick(props.index)();
            }}
          >
            <WbTooltip title="Remove Row" placement="bottom-start">
              <RemoveIcon fontSize="small" />
            </WbTooltip>
          </IconButton>
        )}
      </div>
    </td>
  );
};

interface ColumnHeader {
  property: string;
  title: string;
  required: boolean;
  description?: string;
}

/**
 * Extracts the column headers form the input properties.
 * The column headers are sorted in the same way they are defined in the schema.
 * @param props a complex object containing all the properties definitions, schema, etc
 * @returns a map containing for each property an object that represents it.
 */
function extractColumnHeaders(
  props: ArrayFieldTemplateProps,
): Map<string, ColumnHeader> {
  const distinctColumns = new Map<string, ColumnHeader>();

  // get the required properties. We do not extract the conditional ones since they can be misleading for the rows that do not have such value
  const requiredFields = (props.schema.items as any).required;

  // extract all the properties (even the conditional ones) sorted depending on how they are defined in the schema
  const sortedKeys = Object.keys(props.idSchema).reduce(
    (m, e, i) => m.set(e, i),
    new Map(),
  );

  // we need to show only the columns that have at least one value.
  // the conditional ones are a problem since they can be rendered only if we have at least one row for which they need to be displayed.
  if (props.items.reduce((x, y) => x + Children.count(y.children), 0) === 0) {
    // if there are no rows (children) we can display only the defined ones
    const tableRowItems = (props.schema.items as any)?.properties;

    if (tableRowItems) {
      Object.keys(tableRowItems).forEach(itemName => {
        if (!distinctColumns.has(itemName)) {
          distinctColumns.set(itemName, {
            property: itemName,
            title: tableRowItems[itemName].title,
            required: requiredFields.includes(itemName),
            description: tableRowItems[itemName].description,
          });
        }
      });
    }
  } else {
    // if there are children, we need to display the union of all the columns of all the rows
    props.items.forEach(item =>
      Children.forEach(item.children, child => {
        const childProperties = child.props.schema.properties;
        if (childProperties) {
          Object.keys(childProperties).forEach(property => {
            if (!distinctColumns.has(property)) {
              distinctColumns.set(property, {
                property: property,
                title: childProperties[property].title,
                required: requiredFields.includes(property),
                description: childProperties[property].description,
              });
            }
          });
        }
      }),
    );
  }

  // since the columns can be added to the map in the wrong order (when they are extracted from the children)
  // we need to sort them using the default properties sorting we extracted
  const sortedDistinctColumns = new Map(
    [...distinctColumns.entries()].sort(
      ([keyA, _], [keyB]) => sortedKeys.get(keyA) - sortedKeys.get(keyB),
    ),
  );

  return sortedDistinctColumns;
}
export const ArrayTableContent = (
  props: ArrayFieldTemplateProps & { RowButton?: JSX.Element },
) => {
  const columnHeaders = extractColumnHeaders(props);
  const { maxDescriptionRows } = parseTableTemplateProperties(props);
  const rowClasses = useTruncatedStyles({ maxLines: maxDescriptionRows });
  const [page, setPage] = useState(0);
  const configApi = useApi(configApiRef);
  const configuredRowsPerPage = configApi.getOptionalNumber(
    'mesh.builder.scaffolder.arrayFields.rowsPerPage',
  );

  const rowsPerPage =
    configuredRowsPerPage &&
    Number.isFinite(configuredRowsPerPage) &&
    configuredRowsPerPage > 0
      ? Math.floor(configuredRowsPerPage)
      : undefined;

  const totalRows = props.items.length;
  const previousTotalRowsRef = useRef(totalRows);

  useEffect(() => {
    const previousTotalRows = previousTotalRowsRef.current;

    if (!rowsPerPage) {
      if (page !== 0) {
        setPage(0);
      }
      previousTotalRowsRef.current = totalRows;
      return;
    }

    const lastPage = Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);

    if (totalRows > previousTotalRows) {
      setPage(prevPage => (prevPage === lastPage ? prevPage : lastPage));
    } else if (page > lastPage) {
      setPage(lastPage);
    }

    previousTotalRowsRef.current = totalRows;
  }, [rowsPerPage, totalRows, page]);

  const pagedItems = rowsPerPage
    ? props.items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : props.items;

  const wbColumns: TableCellProps<any>[] = useMemo(() => {
    const itemsUiSchema = (props.uiSchema?.items as Record<string, any>) || {};
    const itemsSchema =
      (props.schema.items as Record<string, any>)?.properties ?? {};
    const cols: TableCellProps<any>[] = Array.from(columnHeaders.entries()).map(
      ([key, header]) => {
        const fieldUiSchema = itemsUiSchema[key] || {};
        const fieldSchema = itemsSchema[key] || {};
        const { width, minWidth, maxWidth } = computeDefaultColumnWidth(
          fieldUiSchema,
          fieldSchema,
        );

        return {
          headerName: `${header.title}${header.required ? ' *' : ''}`,
          headerDescription: header.description ? (
            <WbTooltip
              placement="bottom-start"
              title={
                <Fragment>
                  <WbMarkdownHelperText helperText={header.description} />
                </Fragment>
              }
            >
              <div>
                <WbMarkdownHelperText
                  className={clsx(rowClasses.truncated)}
                  helperText={header.description}
                />
              </div>
            </WbTooltip>
          ) : undefined,
          uniqueFieldId: key,
          resizable: true,
          width: width ?? 'auto',
          autoMinWidth: minWidth,
          autoMaxWidth: maxWidth,
        };
      },
    );
    cols.push({
      headerName: 'Actions',
      uniqueFieldId: 'actions',
      field: 'actions' as any,
      resizable: false,
      width: 'auto',
    });
    return cols;
  }, [columnHeaders, props.uiSchema, props.schema.items, rowClasses.truncated]);

  const pinnedColumns = useMemo(() => {
    const firstColumnId = Array.from(columnHeaders.keys())[0];
    return {
      left: firstColumnId ? [firstColumnId] : [],
      right: ['actions'],
    };
  }, [columnHeaders]);

  return (
    <WbCardContent style={{ padding: 0 }}>
      {props.items.length > 0 ? (
        <WbTable
          // auto layout strictly required here because every column width must is dynamically computed to fit their content by default (a fixed width could be specified in the template)
          enableAutoLayout
          columnPinning={pinnedColumns}
          components={{
            tableContent: {
              columns: wbColumns,
              body: (
                <TableBody>
                  {pagedItems.map(item => (
                    <Fragment key={item.key}>
                      {Children.map(item.children, child =>
                        cloneElement(child, {
                          schema: {
                            ...child.props.schema,
                            columnHeaders: columnHeaders,
                            RowButtons: (
                              <RowButtons {...item} disabled={props.disabled} />
                            ),
                          },
                          disabled: props.disabled,
                        }),
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              ),
            },
          }}
          pagination={
            rowsPerPage
              ? {
                  count: totalRows,
                  limit: rowsPerPage,
                  currentPage: page,
                  rowsPerPageOptions: [],
                  onPageChange: newPage => setPage(newPage),
                  onRowsPerPageChange: () => {},
                }
              : undefined
          }
        />
      ) : null}
    </WbCardContent>
  );
};

export const ArrayTableTemplate = (props: ArrayFieldTemplateProps) => {
  return (
    <WbWidget
      title={
        props.uiSchema?.['ui:title'] ||
        // When in a TableRowTemplate, we want to show the original title of the field
        props.uiSchema?.[UI_TABLE_ROW_TEMPLATE_ORIGINAL_TITLE_KEY] ||
        props.title
      }
      actions={
        props.canAdd ? (
          <WbButton
            label="Add"
            disabled={!props.canAdd}
            icon={<AddIcon />}
            onClick={props.onAddClick}
            color="secondary"
          />
        ) : undefined
      }
    >
      <ArrayTableContent {...props} />
    </WbWidget>
  );
};

export const ArrayTableTemplateLayout = scaffolderPlugin.provide(
  createScaffolderLayout({
    name: 'ArrayTableTemplate',
    component: ArrayTableTemplate as any,
  }),
);
